import { Form, Tabs } from 'antd';
import React from 'react';
import { useBooking } from '../../../../../../../contexts/ticketBookingContext/TicketBookingContext';
import reserveSeatsWithAdditionalServices
  from '../../../../../../../../utils/functions/booking/reserveSeatsWithAdditionalServices';
import purchaseBookedTickets from '../../../../../../../../utils/functions/booking/purchaseBookedTickets';
import AutoNavigationTabItems from '../bookingTypeNavigationTabItems/AutoNavigationTabItems';
import createDefaultStatement from '../../../../../../../../utils/functions/booking/createDefaultStatement';
import useQuery from '../../../../../../../hooks/useQuery';
import autoPassengersFormSet from '../../../../../../../../utils/functions/booking/autoPassengersFormSet';
import BookingNavigationButtons from '../BookingNavigationButtons';
import useTranslation from '../../../../../../../hooks/translation/useTranslation';
import ChosenRideDetails
  from '../../../../../../components/drawers/ticketSearchDrawers/ticketSearchFormDrawer/components/ChosenRideDetails';
import checkPassengers from '../../../../../../../../utils/functions/booking/checkPassengers';

function AutoBookingNavigation({ outline = true }) {
  const { trans } = useTranslation();

  const query = useQuery();
  const guid = query.get('guid');
  const { configInfo } = useTranslation();

  const {
    activeStep,
    departurePassengersForm,
    returnPassengersForm,
    statement,
    services,
    setStatement,
    setActiveStep,
    bookingData,
    departureClassesForm,
    returnClassesForm,
    paymentMethodForm,
    cancelRef,
    setIsNextDisabled,
  } = useBooking();

  const departureClasses = Form.useWatch('classes', departureClassesForm);
  const departureAmountOfClasses = departureClasses
    ?.reduce((acc, cur) => acc + (cur.amount ? cur.amount * 1 : 0), 0);

  const returnClasses = Form.useWatch('classes', returnClassesForm);
  const returnAmountOfClasses = returnClasses
    ?.reduce((acc, cur) => acc + (cur.amount ? cur.amount * 1 : 0), 0);

  const isReturnWay = Boolean(bookingData?.return?.id);

  const lastStep = isReturnWay ? 5 : 3;

  // Define handlers for each step in the process
  const nextStepHandlers = {
    1: async () => {
      if (isReturnWay) {
        setActiveStep(2);
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        if (!(departureAmountOfClasses > 0)) {
          return;
        }
        const res = await createDefaultStatement({
          departureClassesForm,
          guid,
          bookingData,
        });
        if (res?.id) {
          setStatement(res);
          await autoPassengersFormSet({
            passengersForm: departurePassengersForm,
            statementRide: res?.statementRides[0],
          });
          setActiveStep(2);
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      }
    },
    2: async () => {
      if (isReturnWay) {
        if (!(returnAmountOfClasses > 0)) {
          return;
        }
        const res = await createDefaultStatement({
          departureClassesForm,
          returnClassesForm,
          guid,
          bookingData,
        });
        if (res?.id) {
          setStatement(res);
          await autoPassengersFormSet({
            passengersForm: departurePassengersForm,
            statementRide: res?.statementRides[0],
          });
          await autoPassengersFormSet({
            passengersForm: returnPassengersForm,
            statementRide: res?.statementRides[1],
          });
        }
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        setActiveStep(3);
      } else {
        await departurePassengersForm.validateFields();
        checkPassengers({
          form: departurePassengersForm,
          onSuccess: async () => {
            await reserveSeatsWithAdditionalServices({
              departurePassengersForm,
              bookingData,
              statement,
              services,
              setStatement,
              nextStep: () => {
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
                setActiveStep(3);
              },
            });
          },
          trans,
        });
      }
    },
    3: async () => {
      if (isReturnWay) {
        await departurePassengersForm.validateFields();
        checkPassengers({
          form: departurePassengersForm,
          onSuccess: async () => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
            setActiveStep(4);
          },
          trans,
        });
      } else {
        cancelRef.current = 'keep';
        purchaseBookedTickets({
          id: statement.id,
          guid: statement.statementGuid,
          countryCode: configInfo.countryCode,
          payment_card: paymentMethodForm.getFieldValue('payment_card'),
          payment_bank: paymentMethodForm.getFieldValue('payment_bank'),
          trans,
          setIsNextDisabled,
        });
      }
    },
    4: async () => {
      await departurePassengersForm.validateFields();
      await returnPassengersForm.validateFields();
      checkPassengers({
        form: returnPassengersForm,
        onSuccess: async () => {
          await reserveSeatsWithAdditionalServices({
            departurePassengersForm,
            returnPassengersForm,
            bookingData,
            statement,
            services,
            setStatement,
            nextStep: () => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
              setActiveStep(5);
            },
          });
        },
        trans,
      });
    },
    5: async () => {
      cancelRef.current = 'keep';
      purchaseBookedTickets({
        id: statement.id,
        guid: statement.statementGuid,
        countryCode: configInfo.countryCode,
        payment_card: paymentMethodForm.getFieldValue('payment_card'),
        payment_bank: paymentMethodForm.getFieldValue('payment_bank'),
        trans,
        setIsNextDisabled,
      });
    },
  };

  const handleNextStep = () => {
    nextStepHandlers[activeStep]();
  };

  return (
    <div className={`flex flex-col grow p-8 rounded-2xl bg-white ${outline && 'outline-border'} !sticky top-[300px]`}>
      {(bookingData?.departure || bookingData?.return) && (
      <div className="mb-4 hidden lg:flex flex-col gap-4">
        {bookingData?.departure && <ChosenRideDetails chosenRide={bookingData?.departure} />}
        {bookingData?.return && <ChosenRideDetails chosenRide={bookingData?.return} />}
      </div>
      )}
      <span className="max-h-[200px] lg:max-h-full overflow-y-auto lg:overflow-y-hidden">
        <Tabs className="no-tablist" items={AutoNavigationTabItems()} activeKey={activeStep} />
      </span>
      <BookingNavigationButtons
        handleNextStep={handleNextStep}
        activeStep={activeStep}
        isNextDisabled={departureAmountOfClasses < 1
            || (isReturnWay && returnAmountOfClasses < 1 && activeStep > 1)}
        lastStep={lastStep}
      />
    </div>
  );
}

export default AutoBookingNavigation;
